import { ChevronLeft, ChevronRight, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react'
import Breadcrumb from '@/components/ui/Breadcrumb'
import RecentArticles from '@/components/sections/RecentArticles'
import Newsletter from '@/components/sections/Newsletter'

interface OtherProgram {
  id: string
  title: string
  image: string
  href: string
}

const EOAfricaInitiative = () => {
  const otherPrograms: OtherProgram[] = [
    {
      id: 'gmes-africa',
      title: 'GMES and Africa',
      image: 'https://res.cloudinary.com/dlwegkhme/image/upload/v1755761598/9a5c7090884d5e6f246f1c212feaa613c6411912_sgt3cj.png',
      href: '/programmes/gmes-africa'
    }
  ]

  const tags = ['EO for Africa', 'Earth Observation', 'ESA Initiative', 'Research Collaboration', 'Capacity Building']

  const handleShare = (platform: string) => {
    const url = window.location.href
    const title = 'EO for Africa Initiative - African Space Agency'

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank')
        break
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${url}&text=${title}`, '_blank')
        break
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank')
        break
      case 'instagram':
        navigator.clipboard.writeText(url)
        alert('Link copied to clipboard! You can share this on Instagram.')
        break
      default:
        navigator.clipboard.writeText(url)
    }
  }

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Programmes', href: '/programmes/programs' },
    { label: 'EO for Africa Initiative', href: '/programmes/eo-africa-initiative', active: true }
  ]

  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
      {/* Breadcrumb */}
      <div className=" mb-5  ">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Main Content Container */}
      <div className="space-y-16">
        {/* Hero Section */}
        <div className="space-y-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-normal font-pt-serif text-gray-900 leading-tight lg:leading-12">
            EO for Africa Initiative
          </h1>

          <div className="space-y-2">
            <img
              src="https://res.cloudinary.com/dlwegkhme/image/upload/v1757447591/ee190e62c99c30d9e3f1c15d043344c855ae79d6_rabrvd.png"
              alt="Earth Observation for Africa initiative showing satellite monitoring and data analysis systems"
              className="w-full h-64 sm:h-80 md:h-96 lg:h-[502px] object-cover rounded"
            />
            <p className="text-base font-medium font-satoshi text-gray-600">
              Earth Observation for Africa initiative showing satellite monitoring and data analysis systems
            </p>
          </div>
        </div>

        {/* Content and Sidebar Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 order-1">
            <div className="space-y-12">
              {/* Article Content */}
              <div className="space-y-10">
                {/* Introduction */}
                <div className="space-y-6">
                  <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                    The EO for Africa (Earth Observation for Africa) initiative, launched by the European Space Agency (ESA), is designed to enhance Africa's capabilities in Earth Observation (EO) and promote scientific research collaboration between Europe and Africa.
                  </p>

                  <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                    The project builds on the understanding that EO technologies are critical for addressing key challenges on the continent, including food security, climate change, urbanisation, disaster management, and water resource management.
                  </p>
                </div>

                {/* Background and Objectives Section */}
                <div className="space-y-6">
                  <h2 className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900 leading-7">
                    Background and Objectives
                  </h2>

                  <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                    The initiative started with the goal of empowering African researchers and institutions to harness EO data for decision-making and problem-solving in a way that promotes sustainable development. By providing advanced EO tools, data access, cloud computing infrastructure, and expert training, EO for Africa aims to bridge the gap between European EO expertise and Africa's growing demand for such technology.
                  </p>

                  <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                    Launched in 2021, this programme also emphasises capacity-building by ensuring that local African institutions and researchers are equipped with the necessary skills and resources to apply EO solutions in fields like agriculture, climate resilience, and environmental monitoring.
                  </p>

                  <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                    Through the EO for Africa Research and Development Facility, researchers across Africa have gained access to ESA's EO data, cloud platforms, and training. This helps build EO innovation clusters across the continent, fostering collaboration between researchers and local stakeholders to address challenges unique to Africa.
                  </p>
                </div>

                {/* Phase Two Section */}
                <div className="space-y-6">
                  <h2 className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900 leading-7">
                    Phase Two Extension
                  </h2>

                  <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                    In Phase Two of the initiative, launched in 2024 and set to run until 2027, the programme was extended for three more years to consolidate its impact. This phase emphasises further collaboration with African research institutions, increased investment in training, and ensuring data accessibility to support EO-based solutions.
                  </p>
                </div>

                {/* Consortiums Section */}
                <div className="space-y-6">
                  <h2 className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900 leading-7">
                    Consortiums
                  </h2>

                  <div className="space-y-4">
                    <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                      The initiative operates through a consortium of leading European institutions:
                    </p>
                    <ul className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed ml-4 space-y-2 list-none">
                      <li>• Faculty of Geo-Information Science and Earth Observation (ITC) of the University of Twente, the Netherlands</li>
                      <li>• CS GROUP, France</li>
                      <li>• CS GROUP – ROMANIA</li>
                      <li>• Serco Italia S.p.A., Italy</li>
                      <li>• TU Wien, Austria</li>
                      <li>• Vito, Belgium</li>
                    </ul>
                  </div>
                </div>

                {/* Impact and Future Section */}
                <div className="space-y-6">
                  <h2 className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900 leading-7">
                    Impact and Future Vision
                  </h2>

                  <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                    The initiative has made significant strides in building a robust African EO community. By enabling access to state-of-the-art EO tools and fostering cross-continental collaboration, EO for Africa has already contributed to breakthroughs in agricultural management, water resource monitoring, and disaster response in several African nations.
                  </p>

                  <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                    Furthermore, the collaboration between ESA and African institutions has led to the establishment of partnerships in research and innovation, creating long-term benefits for Africa's sustainable development. As the programme moves into its next phase, the vision of creating self-sufficient African EO capabilities is steadily becoming a reality.
                  </p>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-4">
                  {tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-6 py-1.5 bg-gray-50 rounded-full text-base font-medium font-satoshi text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Social Sharing */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <span className="text-lg font-normal font-pt-serif text-gray-600">
                  SHARE
                </span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                    aria-label="Share on Facebook"
                  >
                    <Facebook className="w-full h-full" />
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                    aria-label="Share on Twitter"
                  >
                    <Twitter className="w-full h-full" />
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                    aria-label="Share on LinkedIn"
                  >
                    <Linkedin className="w-full h-full" />
                  </button>
                  <button
                    onClick={() => handleShare('instagram')}
                    className="w-6 h-6 text-gray-800 hover:text-pink-600 transition-colors"
                    aria-label="Share on Instagram"
                  >
                    <Instagram className="w-full h-full" />
                  </button>
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <button className="w-full sm:w-auto px-6 py-3 bg-white rounded-lg border border-gray-300 flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors">
                  <ChevronLeft className="w-5 h-5" />
                  <span className="text-base font-medium font-satoshi text-gray-900">
                    Previous
                  </span>
                </button>

                <button className="w-full sm:w-auto px-6 py-3 bg-white rounded-lg border border-gray-300 flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors opacity-0 pointer-events-none">
                  <span className="text-base font-medium font-satoshi text-gray-900">
                    Next
                  </span>
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 order-2">
            <div className="space-y-10">
              {/* Other Programmes */}
              <div className="space-y-6">
                <h3 className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900 leading-7">
                  OTHER PROGRAMMES
                </h3>

                <div className="space-y-8">
                  {otherPrograms.map((program) => (
                    <a
                      key={program.id}
                      href={program.href}
                      className="group block"
                    >
                      <article className="space-y-4">
                        <div className="w-full h-72">
                          <img
                            src={program.image}
                            alt={program.title}
                            className="w-full h-full object-cover rounded group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <h4 className="text-lg font-normal font-pt-serif text-gray-900 leading-relaxed group-hover:text-wine-100 transition-colors">
                          {program.title}
                        </h4>
                      </article>
                    </a>
                  ))}
                </div>
              </div>

              {/* Related Articles */}
              <RecentArticles />

              {/* Newsletter */}
              <Newsletter />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EOAfricaInitiative